import type { RepositorySettings } from "@auradata/contracts";
import { Button, Card, Field, Input, PageHeader, toast } from "@auradata/ui";
import { useSuspenseQuery } from "@tanstack/react-query";
import { GitBranch } from "lucide-react";
import { useEffect, useState } from "react";
import { repositoryQuery, useSaveRepository } from "@/lib/api/queries";

export function RepositoryPage({ workspace, project }: { workspace: string; project: string }) {
  const { data } = useSuspenseQuery(repositoryQuery(workspace, project));
  const save = useSaveRepository(workspace, project);
  const [d, setD] = useState<RepositorySettings>(data.settings);
  useEffect(() => setD(data.settings), [data.settings]);
  const dirty = JSON.stringify(d) !== JSON.stringify(data.settings);

  return (
    <>
      <PageHeader
        title="Repository"
        meta={
          <span className="flex items-center gap-1.5 font-mono text-fg-subtle">
            <GitBranch className="size-3.5" aria-hidden /> github.com/{data.fullName} · last synced{" "}
            {data.lastSynced}
          </span>
        }
        actions={
          <Button onClick={() => window.open(data.url, "_blank", "noopener")}>
            Open on GitHub
          </Button>
        }
      />
      <Card title="Build settings">
        <div className="flex flex-col gap-4 px-4 py-4">
          <Field
            label="Production branch"
            hint="Scheduled jobs build this branch. Protected on GitHub."
          >
            {({ id }) => (
              <Input
                id={id}
                mono
                value={d.branch}
                onChange={(e) => setD({ ...d, branch: e.target.value })}
                className="w-[260px]"
              />
            )}
          </Field>
          <Field
            label="Project subdirectory"
            hint={
              d.subdirectory === "/"
                ? "dbt_project.yml is at the repository root"
                : `Looks for ${d.subdirectory.replace(/\/$/, "")}/dbt_project.yml`
            }
          >
            {({ id }) => (
              <Input
                id={id}
                mono
                value={d.subdirectory}
                onChange={(e) => setD({ ...d, subdirectory: e.target.value || "/" })}
                className="w-[220px]"
              />
            )}
          </Field>
          <Field label="Pull requests and merges">
            {() => (
              <p className="text-sm text-fg-muted">
                Both are job triggers, not repository settings. A job with the Pull request trigger
                builds every pull request into the CI environment; a job with On merge to main runs
                at the merge commit. Set them under Jobs.
              </p>
            )}
          </Field>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border-subtle px-4 py-2.5">
          {dirty ? (
            <>
              <span className="text-sm text-fg-subtle">Unsaved changes</span>
              <Button onClick={() => setD(data.settings)}>Discard</Button>
            </>
          ) : null}
          <Button
            variant="primary"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() =>
              save.mutate(d, { onSuccess: () => toast.success("Repository settings saved") })
            }
          >
            Save settings
          </Button>
        </div>
      </Card>
    </>
  );
}
